import React, {
    useState
} from 'react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Plus, Trash2
} from 'lucide-react';
import ResourcePicker from '../../components/ResourcePicker';

/**
 * ChannelsSection — channels.mode + authorized_channels[].
 *
 * Channels are picked from the sub-org's existing Channel records via the
 * generic ResourcePicker (one row per allow-listed channel). When mode is
 * "restricted", FEO refuses inbound from any channel not in the list.
 *
 * Pending (unsaved) rows live in local state only — they don't enter
 * `authorized_channels[]` until the user actually picks a channel. This
 * prevents downstream consumers (e.g. AssistantTestPanel's channel select)
 * from seeing empty-string entries that would violate Radix Select's rules.
 */
export default function ChannelsSection({
    a, onPatch, assistantId
}) {
    const ch = a.channels || {
        mode: 'open',
        authorized_channels: []
    };
    const list = Array.isArray(ch.authorized_channels) ? ch.authorized_channels.filter(Boolean) : [];
    const [pendingRows, setPendingRows] = useState(0);

    function setSaved(i, channelId) {
        const next = [...list];
        next[i] = channelId;
        onPatch({
            channels: {
                ...ch,
                authorized_channels: next.filter(Boolean)
            }
        });
    }
    function setPending(channelId) {
        // first pending row that gets a value graduates into the saved list
        if (!channelId) return;
        if (list.includes(channelId)) {
            setPendingRows((n) => Math.max(0, n - 1));
            return;
        }
        onPatch({
            channels: {
                ...ch,
                authorized_channels: [...list, channelId]
            }
        });
        setPendingRows((n) => Math.max(0, n - 1));
    }
    function removeSavedAt(i) {
        onPatch({
            channels: {
                ...ch,
                authorized_channels: list.filter((_, j) => j !== i)
            }
        });
    }
    function removePending() {
        setPendingRows((n) => Math.max(0, n - 1));
    }
    function addRow() {
        setPendingRows((n) => n + 1);
    }

    return (
        <div className="space-y-3">
            <div>
                <Label>Mode</Label>
                <Select value={ch.mode || 'open'} onValueChange={(v) => onPatch({
                    channels: {
                        ...ch,
                        mode: v
                    }
                })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="open">Open — any channel allowed</SelectItem>
                        <SelectItem value="restricted">Restricted — only authorized channels</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Authorized Channels</Label>
                {list.length === 0 && pendingRows === 0 ? (
                    <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11.5px] text-muted-foreground">
                        No channels authorized yet. Click <b>+ Add channel</b> below to pick one.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {list.map((id, i) => (
                            <div key={`saved-${i}`} className="grid grid-cols-[1fr_auto] items-start gap-2">
                                <ResourcePicker
                                    kind="channel"
                                    value={id}
                                    onChange={(picked) => setSaved(i, picked)}
                                    placeholder="No channel selected"
                                    defaultAssistantId={assistantId}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeSavedAt(i)}
                                    className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                    title="Remove"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                        {Array.from({
                            length: pendingRows
                        }).map((_, i) => (
                            <div key={`pending-${i}`} className="grid grid-cols-[1fr_auto] items-start gap-2">
                                <ResourcePicker
                                    kind="channel"
                                    value=""
                                    onChange={(picked) => setPending(picked)}
                                    placeholder="Pick a channel…"
                                    defaultAssistantId={assistantId}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={removePending}
                                    className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                    title="Discard"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRow}>
                    <Plus className="mr-1 h-3 w-3" /> Add channel
                </Button>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                    Channels are managed under <b>Library → Channels</b>. Pick from your existing list here.
                </div>
            </div>
        </div>
    );
}
